import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

function JuvenileForm({ onSubmit, initialData }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    gender: 'Male',
    primary_officer_id: '',
    // Contact Info
    phone: '',
    address: '',
    // Aliases
    aliases: '',
    // Physical Description
    height: '',
    weight: '',
    hairColor: '',
    eyeColor: '',
    // School Info
    schoolName: '',
    gradeLevel: '',
    attendanceStatus: 'Regular',
    // Employment Info
    employerName: '',
    jobTitle: '',
  });
  const [dob, setDob] = useState(null);
  const [staffUsers, setStaffUsers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await apiClient.get('/api/users');
        setStaffUsers(res.data);
      } catch (err) { console.error("Failed to fetch users", err); }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (initialData) {
      setDob(initialData.date_of_birth ? new Date(initialData.date_of_birth) : null);
      setFormData({
        first_name: initialData.first_name || '',
        last_name: initialData.last_name || '',
        gender: initialData.gender || 'Male',
        primary_officer_id: initialData.primary_officer_id || '',
        phone: initialData.contact_info?.phone || '',
        address: initialData.contact_info?.address || '',
        aliases: (initialData.aliases || []).join(', '),
        height: initialData.physical_description?.height || '',
        weight: initialData.physical_description?.weight || '',
        hairColor: initialData.physical_description?.hairColor || '',
        eyeColor: initialData.physical_description?.eyeColor || '',
        schoolName: initialData.school_info?.name || '',
        gradeLevel: initialData.school_info?.grade || '',
        attendanceStatus: initialData.school_info?.attendance || 'Regular',
        employerName: initialData.employment_info?.employer || '',
        jobTitle: initialData.employment_info?.title || '',
      });
    }
  }, [initialData]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { phone, address, height, weight, hairColor, eyeColor, aliases, schoolName, gradeLevel, attendanceStatus, employerName, jobTitle, ...rest } = formData;
    const finalData = {
      ...rest,
      date_of_birth: dob ? dob.toISOString().split('T')[0] : null,
      contact_info: { phone, address },
      physical_description: { height, weight, hairColor, eyeColor },
      aliases: aliases ? aliases.split(',').map(alias => alias.trim()) : [],
      school_info: { name: schoolName, grade: gradeLevel, attendance: attendanceStatus },
      employment_info: { employer: employerName, title: jobTitle },
    };
    onSubmit(finalData);
  };

  return (
    <form onSubmit={handleSubmit} className="card p-6 space-y-8">
      {/* Personal Info Section */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-slate-700 border-b pb-2">Personal Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-2"><label className="label">First Name:</label><input type="text" name="first_name" value={formData.first_name} onChange={handleChange} required /></div>
          <div className="lg:col-span-2"><label className="label">Last Name:</label><input type="text" name="last_name" value={formData.last_name} onChange={handleChange} required /></div>
          <div className="lg:col-span-4"><label className="label">Aliases/Nicknames (comma-separated):</label><input type="text" name="aliases" value={formData.aliases} onChange={handleChange} /></div>
          <div><label className="label">Date of Birth:</label><DatePicker selected={dob} onChange={(date) => setDob(date)} className="w-full" dateFormat="dd/MM/yyyy" /></div>
          <div><label className="label">Gender:</label><select name="gender" value={formData.gender} onChange={handleChange}><option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option></select></div>
        </div>
      </div>
      
      {/* Physical Description Section */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-slate-700 border-b pb-2">Physical Description</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div><label className="label">Height:</label><input type="text" name="height" value={formData.height} onChange={handleChange} placeholder="e.g., 5' 10&quot;" /></div>
          <div><label className="label">Weight:</label><input type="text" name="weight" value={formData.weight} onChange={handleChange} placeholder="e.g., 150 lbs" /></div>
          <div><label className="label">Eye Color:</label><input type="text" name="eyeColor" value={formData.eyeColor} onChange={handleChange} /></div>
          <div><label className="label">Hair Color:</label><input type="text" name="hairColor" value={formData.hairColor} onChange={handleChange} /></div>
        </div>
      </div>

      {/* Social & Educational Section */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-slate-700 border-b pb-2">Social & Educational</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div><label className="label">School Name:</label><input type="text" name="schoolName" value={formData.schoolName} onChange={handleChange} /></div>
          <div><label className="label">Grade Level:</label><input type="text" name="gradeLevel" value={formData.gradeLevel} onChange={handleChange} /></div>
          <div className="md:col-span-2"><label className="label">Attendance Status:</label><select name="attendanceStatus" value={formData.attendanceStatus} onChange={handleChange}><option value="Regular">Regular</option><option value="Irregular">Irregular</option><option value="Truant">Truant</option><option value="Not Enrolled">Not Enrolled</option></select></div>
          <div><label className="label">Employer:</label><input type="text" name="employerName" value={formData.employerName} onChange={handleChange} /></div>
          <div><label className="label">Job Title:</label><input type="text" name="jobTitle" value={formData.jobTitle} onChange={handleChange} /></div>
        </div>
      </div>

       {/* Contact & Assignment Section */}
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-slate-700 border-b pb-2">Contact & Assignment</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div><label className="label">Phone:</label><input type="text" name="phone" value={formData.phone} onChange={handleChange} /></div>
          <div><label className="label">Address:</label><input type="text" name="address" value={formData.address} onChange={handleChange} /></div>
          <div className="md:col-span-2"><label className="label">Primary Officer:</label><select name="primary_officer_id" value={formData.primary_officer_id} onChange={handleChange}><option value="">-- Select an Officer --</option>{staffUsers.map(user => (<option key={user.id} value={user.id}>{user.full_name}</option>))}</select></div>
        </div>
      </div>

      <div className="flex justify-end gap-4 pt-4 mt-6 border-t border-slate-200">
        <button type="button" onClick={() => navigate(-1)} className="bg-slate-200 text-slate-700 hover:bg-slate-300 font-bold py-2 px-4 rounded-lg">Cancel</button>
        <button type="submit" className="bg-gold text-white hover:bg-gold-dark font-bold py-2 px-4 rounded-lg">Save Profile</button>
      </div>
    </form>
  );
}

// Simple helper to avoid repeating classes
const Label = ({ children }) => <label className="block text-sm font-medium text-slate-700 mb-1">{children}</label>;


export default JuvenileForm;